/**
 * Standalone Subscription Management Page
 * Matches ParkProUI design system
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Crown, ArrowRight, Info, ArrowLeft } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import { isAgentAdmin } from '../../../utils/roleUtils';
import toast from '../../../utils/toast';

const Subscription = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/billing/plans`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const allPlans = data.plans || [];
        
        // Filter plans: exclude tier_level >= 998 (bundles/extras)
        let filteredPlans = allPlans.filter(plan => plan.tier_level < 998);
        
        // If user is agentAdmin, only show agency plans (plans with "agency" in plan_id)
        if (isAgentAdmin(user?.role)) {
          filteredPlans = filteredPlans.filter(plan => {
            const planId = (plan.plan_id || '').toLowerCase();
            return planId.includes('agency');
          });
        }
        
        setPlans(filteredPlans);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/subscription-status`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data.subscription);
        if (data.subscription) {
          setBillingCycle(data.subscription.billing_cycle || 'monthly');
        }
      }
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
    }
  };

  const formatPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
    const monthlyPrice = billingCycle === 'yearly' ? Math.round(price / 12) : price;
    return monthlyPrice;
  };

  const getYearlyDiscount = (plan) => {
    if (!plan.price_yearly) return 0;
    const yearlyMonthly = plan.price_yearly / 12;
    const discount = Math.round(((plan.price_monthly - yearlyMonthly) / plan.price_monthly) * 100);
    return discount;
  };

  const handleSelectPlan = (plan) => {
    // Prevent agentAdmin from selecting solo plans (safety check)
    if (isAgentAdmin(user?.role)) {
      const planId = (plan.plan_id || '').toLowerCase();
      if (!planId.includes('agency')) {
        toast.error('Agency administrators can only select agency plans');
        return;
      }
    }
    setSelectedPlan(plan);
  };

  const handleContinue = () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    // Navigate to checkout page
    navigate(`/agent/settings/subscription/checkout`, {
      state: {
        plan: selectedPlan,
        billingCycle,
        currentSubscription
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/agent/settings')}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Settings</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Manage Your Subscription
          </h1>
          <p className="text-sm text-gray-600">
            Choose the plan that best fits your needs
          </p>
        </div>

        {/* Current Subscription */}
        {currentSubscription && (
          <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">Current Plan:</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                    {currentSubscription.status?.toUpperCase() || 'ACTIVE'}
                  </span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {currentSubscription.plan_name || currentSubscription.plan_id}
                </p>
                <p className="text-xs text-gray-600 capitalize">
                  {currentSubscription.billing_cycle || 'monthly'} billing • ${currentSubscription.price_monthly || '0'}/month
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-center gap-4">
            <span className="text-sm text-gray-700 font-medium">Monthly</span>
            <div className="relative flex items-center bg-gray-100 rounded-lg p-1 border border-gray-300">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`
                  px-4 py-2 rounded-md text-sm font-medium transition-all
                  ${billingCycle === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-300'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`
                  px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-1.5
                  ${billingCycle === 'yearly'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-300'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                Yearly
                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                  Save 11%
                </span>
              </button>
            </div>
            <span className="text-sm text-gray-700 font-medium">Yearly</span>
          </div>
        </div>

        {/* Pricing Notice */}
        <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 leading-relaxed">
              All prices are shown in USD. Taxes are not currently charged. If applicable taxes are required in the future, they will be calculated at checkout based on your billing location.
            </p>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan?.plan_id === plan.plan_id;
            const isCurrent = currentSubscription?.plan_id === plan.plan_id;
            const isHighlighted = plan.is_best_value || plan.is_enterprise_recommended;
            const monthlyPrice = formatPrice(plan);
            const yearlyDiscount = getYearlyDiscount(plan);

            return (
              <div
                key={plan.plan_id}
                onClick={() => !plan.is_decoy && handleSelectPlan(plan)}
                className={`
                  relative bg-white rounded-lg border transition-all cursor-pointer shadow-sm
                  ${isSelected 
                    ? 'border-blue-600 shadow-md ring-2 ring-blue-600 ring-opacity-20' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }
                  ${plan.is_decoy ? 'opacity-50 cursor-not-allowed' : ''}
                  ${isHighlighted && !isSelected ? 'border-blue-500' : ''}
                `}
              >
                {/* Badges */}
                {isCurrent && (
                  <div className="absolute -top-2.5 left-4 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded border border-green-200">
                    Current Plan
                  </div>
                )}
                {plan.is_best_value && (
                  <div className="absolute -top-2.5 right-4 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded flex items-center gap-1">
                    <Star size={10} />
                    Best Value
                  </div>
                )}
                {plan.is_enterprise_recommended && !plan.is_best_value && (
                  <div className="absolute -top-2.5 right-4 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded flex items-center gap-1">
                    <Crown size={10} />
                    Recommended
                  </div>
                )}

                <div className="p-6 flex flex-col h-full">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {plan.display_name}
                  </h3>
                  {plan.description && (
                    <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                  )}

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-2xl font-bold text-gray-900">
                      ${monthlyPrice}
                    </span>
                    <span className="text-sm text-gray-600">/month</span>
                    {billingCycle === 'yearly' && yearlyDiscount > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                        Save {yearlyDiscount}%
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  {plan.features && Object.values(plan.features).flat().length > 0 && (
                    <ul className="space-y-2 mb-6 flex-1">
                      {Object.values(plan.features)
                        .flat()
                        .filter(feature => {
                          return !feature.is_boolean_feature || feature.feature_value === 'true';
                        })
                        .slice(0, 6)
                        .map((feature, idx) => {
                          const description = feature.description.charAt(0).toUpperCase() + feature.description.slice(1);
                          return (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                              <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span>
                                {feature.is_boolean_feature 
                                  ? description
                                  : `${feature.feature_value} ${description}`
                                }
                              </span>
                            </li>
                          );
                        })
                      }
                    </ul>
                  )}

                  {/* Selection Indicator */}
                  <div className="mt-auto pt-4 border-t border-gray-200">
                    {isSelected ? (
                      <div className="flex items-center justify-center gap-2 text-blue-600 font-semibold text-sm">
                        <Check className="w-4 h-4" />
                        <span>Selected</span>
                      </div>
                    ) : (
                      <div className="text-center text-sm text-gray-500">
                        Click to select
                      </div>
                    )}
                  </div>

                  {/* Decoy Notice */}
                  {plan.is_decoy && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 text-center">
                        Contact sales for enterprise pricing
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            disabled={!selectedPlan}
            className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-sm transition-all duration-200 hover:from-blue-500 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
          >
            Continue to Checkout
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
